import { constant, groupBy } from 'lodash';
import pluralize from 'pluralize';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import { FolderIcon } from '@air/design-icons';
import {
  contentCollectionApplyAllSelector,
  contentCollectionClipsSelector,
  contentCollectionFormSelector,
} from '@air/feature-content-collection/redux';
import { useBreakpointsContext } from '@air/provider-media-query';

import { ContentCollectionGalleryAsset } from '~/components/ContentCollection/ContentCollectionGalleryAsset';
import { useObserveTranscodingClipsUpdates } from '~/components/ContentCollection/hooks/useObserveTranscodingClipsUpdates';
import { GalleryItemType } from '~/components/Gallery/types';
import { useGalleryDynamicSections } from '~/components/Gallery/useGalleryDynamicSections';
import {
  SelectableGalleryView,
  type SelectableGalleryViewProps,
} from '~/components/SelectableGalleryView/SelectableGalleryView';
import { setSelectableGalleryItemsAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';

export type ContentCollectionGalleryViewProps = Pick<SelectableGalleryViewProps, 'scrollElementRef'>;

const CUSTOM_FIELD_ROW_HEIGHT_PX = 62;
const CUSTOM_FIELD_ROW_PADDING_PX = 4;
const CUSTOM_FIELD_ROW_GAP_PX = 8;
const GALLERY_CONTAINER_WIDTH_INSET_PX = 16;
const GALLERY_CONTAINER_HORIZONTAL_PADDING_DESKTOP_PX = 20;
const GALLERY_CONTAINER_HORIZONTAL_PADDING_MOBILE_PX = 16;
const GALLERY_ITEM_WIDTH_DESKTOP_PX = 235;

export const ContentCollectionGalleryView = memo(({ scrollElementRef }: ContentCollectionGalleryViewProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const dispatch = useDispatch();
  const clips = useSelector(contentCollectionClipsSelector);
  const containerRef = useRef<HTMLDivElement>(null);
  const applyAll = useSelector(contentCollectionApplyAllSelector);
  const form = useSelector(contentCollectionFormSelector);

  const assetHeight = useMemo(() => {
    const baseHeight = 281;
    const customFields = form?.fields.filter((field) => field.customFieldId);

    if (!applyAll && customFields?.length) {
      const totalCustomFieldHeight = customFields.length * CUSTOM_FIELD_ROW_HEIGHT_PX + CUSTOM_FIELD_ROW_PADDING_PX;
      const totalCustomFieldGap = (customFields.length - 1) * CUSTOM_FIELD_ROW_GAP_PX;

      // const totalCustomFieldGap = (customFields.length - 2) * 4;
      return baseHeight + totalCustomFieldHeight + totalCustomFieldGap;
    }

    return baseHeight;
  }, [applyAll, form?.fields]);

  // add extra space to row for wider drop-rearrange area
  const { width = 0, ref: widthRef } = useResizeDetector<HTMLDivElement>({
    handleHeight: false,
    refreshMode: 'throttle',
    refreshRate: 300,
  });

  useEffect(() => {
    const selectableItems: SelectableGalleryItem[] = clips.map((clip) => ({
      id: clip.id,
      type: GalleryItemType.file,
      item: clip,
    }));

    dispatch(setSelectableGalleryItemsAction({ selectableItems, persist: false }));
  }, [dispatch, clips]);

  useObserveTranscodingClipsUpdates(clips);

  const clipGroups = useMemo(() => groupBy(clips, 'groupName'), [clips]);

  const galleryDynamicSections = useGalleryDynamicSections({
    renderFile: ({ data, index, box }) => <ContentCollectionGalleryAsset data={data} index={index} box={box} />,
    sections: Object.entries(clipGroups).map(([groupName, clips]) => ({
      groupName,
      data: {
        total: clips.length,
        hasMore: false,
        isLoading: false,
        items: clips,
      },
      renderTitle: ({ total }) => {
        const title = groupName.split('|').pop() || 'Files';

        const isFiles = groupName === 'Files';

        return (
          <div className="flex items-center gap-2">
            {isFiles ? (
              <div className="flex-1 font-bold text-12">{pluralize('file', total, true)}</div>
            ) : (
              <>
                <FolderIcon size="sm" />
                <div className="flex-1 font-bold text-12">{title.split('/').join(' / ')}</div>
                <div className="font-bold text-12">({pluralize('file', total, true)})</div>
              </>
            )}
          </div>
        );
      },
    })),
    containerWidth: width - (isAboveMediumScreen ? GALLERY_CONTAINER_WIDTH_INSET_PX : 0),
    containerHorizontalPadding: isAboveMediumScreen
      ? GALLERY_CONTAINER_HORIZONTAL_PADDING_DESKTOP_PX
      : GALLERY_CONTAINER_HORIZONTAL_PADDING_MOBILE_PX,
    itemWidth: isAboveMediumScreen ? GALLERY_ITEM_WIDTH_DESKTOP_PX : width,
    itemHeight: assetHeight,
    itemHorizontalSpacing: 16,
    isSelectable: constant(true),
    sortComparator: (a, b) => {
      const aTitle = a?.groupName.split('|').pop() || 'Files';
      const bTitle = b?.groupName.split('|').pop() || 'Files';

      const aIsFiles = a?.groupName === 'Files';
      const bIsFiles = b?.groupName === 'Files';

      // Put "Files" at the end
      if (aIsFiles && bIsFiles) {
        return 0;
      }
      if (aIsFiles) {
        return -1;
      }
      if (bIsFiles) {
        return 1;
      }

      // Otherwise alphabetical order
      return aTitle.localeCompare(bTitle);
    },
  });

  return (
    <>
      <div ref={widthRef} className="w-full" />
      {width ? (
        <div className="relative min-h-full w-full pt-0 md:px-1 md:pt-7" ref={containerRef}>
          <SelectableGalleryView
            scrollElementRef={scrollElementRef}
            containerRef={containerRef}
            sections={galleryDynamicSections.filter((section) => !!section)}
            width={width}
          />
        </div>
      ) : null}
    </>
  );
});

ContentCollectionGalleryView.displayName = 'ContentCollectionGalleryView';
